/**
 * The sky from the ground (ruling 22): inside a planet's atmosphere the sky is sky, and distance hazes.
 * The game only ever drew an atmosphere as a rim seen from space, so at layer 0 the sky behind a
 * soldier was black space. Now, as the camera goes below a planet's atmosphere rim (1.07 R):
 *
 *   - (heights are measured above the ground under the camera, not the planet's sphere)
 *   - a sky dome around the camera fades in, in the planet's own atmosphere colour (its rim colour,
 *     lightened), as strong as its atmosphere (a thin one leaves a darker sky);
 *   - the stars and nebula fade out by as much;
 *   - distance hazes into the sky colour (scene fog) on the planet you are on only — the other
 *     planets in the sky stay visible, because that is where the shots come from. The haze runs from
 *     about half your horizon to four horizons, so it follows the person or the vehicle alike.
 *
 * The sky follows the sun: where the game's sun is up it is the day sky, where it is down the dome
 * darkens and the stars come back, with dusk between (the sun is the scene's main directional light).
 *
 * Above the atmosphere (the turn game's orbit view) all of it is off: nothing changes there.
 *
 * 67.40 (decision 140: simple and readable, not simulated) — the sky is a stand-in per world. On an air world the dome
 * is a gradient, horizon to zenith, warmer toward the sun's side and warmest at dusk (the coolest skies take the warmest
 * gradients, per the owner), darkening and thinning at night so the stars come through. A tiny world (the smallest size
 * class) holds no air: no dome, no haze — cool bare black, sharp stars, hard light — and whatever else stands in its sky
 * (a neighbour, a ring, a giant: they are the scene's own lit meshes) shows plain. One sun direction (the scene's main
 * light) lights every planet the same way. One dome, no lights; the terrain palette untouched.
 * Presentation only.
 */
import * as THREE from 'three';
import type { PlanetState, PlanetArchetype } from '../types';
import { AIR, airOf, airTypeOf, sunExtinction, tintLuminance, duskOf, type AirProfile } from '../core/air';
import type { PlanetMeshData } from './renderer';
import { getSurfaceRadius } from '../core/surface';
import { shapedGround } from '../core/shapingGround';
import { fogForSight } from './weatherView';
import { FINE_FADE } from './groundLift';
import { HAZE_LIFT } from './hazeLift';

/** The weather's hand on the sky (rendering/weatherView.ts): a tint and how far toward it, and the drawn range the fog band closes to (world units; Infinity = clear) — the sim's sight, or further for a kind drawn lighter (389: haze at 2 ×). */
export interface SkyWeather { tint: THREE.Color; tintAmount: number; sight: number }

/** A material that takes scene fog (Three's base Material type does not declare the flag). */
type FogMaterial = THREE.Material & { fog?: boolean };

/** How far inside a planet's atmosphere the camera is: 0 at the rim (`rimOverR` radii out, core/air.ts: over the summits) and above, 1 on the ground. */
export function insideAtmosphere(altitude: number, radius: number, rimOverR: number): number {
  const depth = radius * (rimOverR - 1);
  return Math.max(0, Math.min(1, 1 - altitude / depth));
}

/**
 * 74.10: the atmosphere rim (renderer.ts's Fresnel shell at 1.07 R) and a haze-style cloud shell (cloudLayer.ts, 1.05–1.10 R)
 * are spheres at one altitude, and a human-scale world's ground stands through them (5–45 % of a large world is above 1.07 R):
 * from a hill the camera is outside the shell and sees it as a transparent sheet at head height, cut by the ground — the
 * purple / green sheets of DECISIONS 361. The rim is a far look (the planet's limb from orbit or from another world): it
 * fades out over `RIM_FADE_OVER` radii above the rim, so near the ground only the dome and the fog are the air. The cloud
 * shell fades out while the camera is within `HAZE_BAND` of its altitude (and back over `HAZE_BAND_FADE` beyond), so it
 * never cuts through the viewer; above or below it, it is the haze over the valleys or overhead.
 */
export const RIM_FADE_OVER = 0.25;
export const HAZE_BAND = 0.03;
export const HAZE_BAND_FADE = 0.03;
function smooth(t: number): number { return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); }
/** The rim's intensity factor at `distOverR` radii from the planet's centre: 0 at and under the rim (`rimOverR`), 1 from `RIM_FADE_OVER` further out. */
export function rimGlowFade(distOverR: number, rimOverR: number): number {
  return smooth((distOverR - rimOverR) / RIM_FADE_OVER);
}
/** A cloud shell's opacity factor: 0 while the camera is within `HAZE_BAND` radii of the shell's altitude `layerOverR`, 1 past `HAZE_BAND + HAZE_BAND_FADE`. */
export function hazeBandFade(distOverR: number, layerOverR: number): number {
  return smooth((Math.abs(distOverR - layerOverR) - HAZE_BAND) / HAZE_BAND_FADE);
}

/**
 * The sky by planet type, seen from the ground: a colour and how thick the air is (the dome's opacity on the ground) —
 * the air's row (core/air.ts AIR_TYPES: temperate and tidal worlds a blue sky, cold ones a pale one, volcanic ash, arid
 * amber dust, fungal a violet haze, sonora lavender, overgrowth a green haze), the strength scaled with the density when
 * the planet's size is known (74.10 step 1: a small world's air is thinner).
 */
export function skyOf(planet: Pick<PlanetState, 'archetype' | 'palette'> & Partial<Pick<PlanetState, 'radius' | 'heightmap'>>): { color: THREE.Color; strength: number } {
  const t = airTypeOf(planet);
  const strength = planet.radius !== undefined && planet.heightmap !== undefined ? airOf(planet as Pick<PlanetState, 'archetype' | 'palette' | 'radius' | 'heightmap'>).strength : t.sky.strength;
  return { color: new THREE.Color(t.sky.color), strength };
}

/**
 * 67.40: each air world's sky gradient — the zenith's colour, the horizon's, the warm colour toward the sun, and how far
 * the sun's side warms by day (dusk warms it further). The coolest skies get the warmest gradients (the owner); an amber
 * or ash sky, already warm, gets less. The dome's thickness is SKIES' strength.
 */
export interface SkyLook { zenith: THREE.Color; horizon: THREE.Color; warm: THREE.Color; warmth: number; strength: number }

/** 67.40: a world's sky gradient (the air's row, core/air.ts), or null when it holds no air (a tiny world: bare space). */
export function skyLook(planet: Pick<PlanetState, 'archetype' | 'palette' | 'radius'> & Partial<Pick<PlanetState, 'heightmap'>>): SkyLook | null {
  if (planet.heightmap ? airOf(planet as Pick<PlanetState, 'archetype' | 'palette' | 'radius' | 'heightmap'>).airless : planet.radius < AIR.airlessUnder) return null;
  const base = skyOf(planet), t = airTypeOf(planet).look;
  return { zenith: new THREE.Color(t.zenith), horizon: new THREE.Color(t.horizon), warm: new THREE.Color(t.warm), warmth: t.warmth, strength: base.strength };
}

/** 67.40: how much of dusk it is where the sun stands `elevation` — 1 with the sun on the horizon, gone well above or below it (74.10 step 2: sized by the air's density, 1 as before). */
export function dusk(elevation: number, density = 1): number {
  return duskOf(elevation, density);
}

/**
 * 67.40: the sky dome's material — a gradient by the view's height over the local horizon, warming toward the sun's
 * side (more at dusk), dimmed by night; its uniforms set by `setSkyDome`. Back faces of a sphere round the camera.
 */
export function skyDomeMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uZenith: { value: new THREE.Color() }, uHorizon: { value: new THREE.Color() }, uWarm: { value: new THREE.Color() }, uTint: { value: new THREE.Color() },
      uUp: { value: new THREE.Vector3(0, 1, 0) }, uSun: { value: new THREE.Vector3(0, 1, 0) },
      uWarmth: { value: 0 }, uDay: { value: 1 }, uDusk: { value: 0 }, uTintAmount: { value: 0 }, uOpacity: { value: 0 },
    },
    vertexShader: `varying vec3 vWorld;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vWorld = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform vec3 uZenith, uHorizon, uWarm, uTint, uUp, uSun; uniform float uWarmth, uDay, uDusk, uTintAmount, uOpacity;
      varying vec3 vWorld;
      void main() {
        vec3 dir = normalize(vWorld - cameraPosition);
        float h = dot(dir, uUp), t = smoothstep(0.0, 0.65, max(h, 0.0));
        vec3 col = mix(uHorizon, uZenith, pow(t, 0.7));
        vec3 flatDir = dir - uUp * h, flatSun = uSun - uUp * dot(uSun, uUp);
        float side = (length(flatDir) > 1e-4 && length(flatSun) > 1e-4) ? max(0.0, dot(normalize(flatDir), normalize(flatSun))) : 0.0;
        float low = 1.0 - t * 0.8;
        col = mix(col, uWarm, clamp(pow(side, 5.0) * low * uWarmth * uDay * 0.6, 0.0, 1.0));   // by day, a gentle warmth on the sun's side
        col = mix(col, uTint, uTintAmount);                               // the weather's hand (weatherView)
        col *= 0.07 + 0.93 * uDay;                                       // night
        col += uWarm * uDusk * (0.6 * pow(side, 1.6) * low + 0.14 * (1.0 - t)) * (1.0 - 0.7 * uTintAmount);   // dusk: the sun's side glows, the horizon all round a little
        gl_FragColor = vec4(col, uOpacity);
      }`,
    transparent: true, depthWrite: false, side: THREE.BackSide, fog: false,
  });
}

/** 67.40: set the dome for a look, the local up, the sun's direction, and the weather's tint (0 = none). */
export function setSkyDome(mat: THREE.ShaderMaterial, look: SkyLook, up: THREE.Vector3, sun: THREE.Vector3, opacity: number, tint: THREE.Color | null = null, tintAmount = 0, density = 1): void {
  const u = mat.uniforms, el = up.dot(sun);
  u.uZenith.value.copy(look.zenith); u.uHorizon.value.copy(look.horizon); u.uWarm.value.copy(look.warm);
  u.uUp.value.copy(up); u.uSun.value.copy(sun);
  u.uWarmth.value = look.warmth; u.uDay.value = daylight(el); u.uDusk.value = dusk(el, density); u.uOpacity.value = opacity;
  if (tint) u.uTint.value.copy(tint);
  u.uTintAmount.value = tintAmount;
}

/** How much day it is where the sun stands `elevation` (the sine of its height over the local horizon): 0 at night, 1 by day, a smooth dusk from just below the horizon to a little above. */
export function daylight(elevation: number): number {
  const d = Math.max(0, Math.min(1, (elevation + 0.12) / 0.4));
  return d * d * (3 - 2 * d);
}

/**
 * 74.15 (DECISIONS 384, the owner: "some weird fog behavior that seems to happen on them"): the haze's height is the
 * person's — the boom's height over the feet the host passes (`camera.userData.hazeHeight`, game/actionHost.ts: the
 * shoulder view), else the camera's over the ground at the fine patch's focus with the landforms' records on it
 * (`hazeGround`, core/shapingGround.ts) — not the camera's over the coarse mesh under it. The shoulder camera stands behind
 * and above the person: on a steep facet the mesh under it was near or far below as you turned (the haze's far ran 242–946
 * people by facing alone, 332–410 on the plains), and a band's shelf or a crest stood up to tens of people over the mesh
 * (sweeps/2026-10-30-74.15-mountains-look.md). This is the fallback's ground: at the patch's focus with the records on it
 * (a world point); null when no patch is drawn on `planet` (its focus not on this planet's mesh).
 */
export function hazeGround(planet: PlanetState, focus: { x: number; y: number; z: number }, focusOn: boolean): { x: number; y: number; z: number } | null {
  const fl = Math.hypot(focus.x - planet.position.x, focus.y - planet.position.y, focus.z - planet.position.z);
  if (!focusOn || !(fl > 0.5 * planet.radius && fl < 1.5 * planet.radius)) return null;
  const d = { x: (focus.x - planet.position.x) / fl, y: (focus.y - planet.position.y) / fl, z: (focus.z - planet.position.z) / fl }, coarse = getSurfaceRadius(planet, d);
  if (Math.abs(fl - coarse) > 1e-4 * planet.radius) return null;   // (the patch stands its focus on its planet's mesh: one off it is another world's)
  const r = planet.shapings?.length ? shapedGround(planet, d.x, d.y, d.z, coarse) : coarse;
  return { x: planet.position.x + d.x * r, y: planet.position.y + d.y * r, z: planet.position.z + d.z * r };
}
/** 74.15: the haze's height eases over this many seconds (a step off a shelf, a turn); a change past `snap` × (a view's switch) is taken at once. */
export const HAZE_EASE = { seconds: 0.4, snap: 3 };

/**
 * The height the shoulder camera passes the haze (`camera.userData.hazeHeight`): the boom's own height over the feet, never
 * under the eye. Pitched up, the boom swings down behind the person, under the feet near the limit, and a height near zero
 * closed the haze to a person or two: the ground went the fog's colour (the owner: "if you look too steep up … everything
 * becomes bright white but you can still see basic outlines").
 */
export function hazeHeightOfBoom(boomRise: number, eyeHeight: number): number {
  return Math.max(boomRise, eyeHeight);
}

export class SkyHaze {
  private readonly dome: THREE.Mesh;
  private readonly domeMat: THREE.ShaderMaterial;
  private readonly fog: THREE.Fog;
  private skyLayers: Array<{ mat: FogMaterial & { opacity: number }; base: number }> = [];
  private hazed: number | null = null;   // the planet whose terrain takes the fog
  private eased: { planet: number; height: number; at: number } | null = null;   // 74.15: the haze's height, eased
  private readonly sunDir = new THREE.Vector3(40, 50, 30).normalize();
  private sunLight: THREE.DirectionalLight | null = null;
  private sunLightDistance = 1;
  /** the key light as the scene made it: the sun overhead in no air (74.10 step 2 tints it through the air) */
  private sunBase: { color: THREE.Color; intensity: number } | null = null;
  /** 74.10 step 2: the sun's transmission through the air at the camera this frame (white in space or overhead) — the key light's tint, and the sun disc's (systemSky) */
  readonly sunTint = new THREE.Color(1, 1, 1);
  /** 74.10 step 2b: the air's density at the camera this frame (the world's, eased by how deep in the air the camera is; 0 in space) — the stars' extinction (starSky) */
  densityNow = 0;
  /** The direction to the sun (67.73: the far field's lamps read night where each stands). */
  get sun(): THREE.Vector3 { return this.sunDir; }
  /** 74.2 step 0 (DECISIONS 353): the sky's clock turns the sun — the scene's key light follows, and every reader of the day (the dome, the lamps, the stars, the system in the sky) reads the turned direction next frame. */
  setSun(dir: THREE.Vector3): void {
    this.sunDir.copy(dir).normalize();
    if (this.sunLight) this.sunLight.position.copy(this.sunDir).multiplyScalar(this.sunLightDistance);
  }
  /** 67.30: how far the stars show this frame (1 in space or at night; 0 under a full day sky), and the local up inside an atmosphere (null in open space) — for the shooting stars. */
  starsShown = 1;
  private readonly upAt = new THREE.Vector3();
  localUp: THREE.Vector3 | null = null;
  /** 74.1: the day at the camera (0 night, 1 full day; 1 in open space), the air's thickness (SKIES strength; 0 airless or in space) and the horizon's colour as shown — what the system in the sky fades against. */
  daylightNow = 1;
  airNow = 0;
  readonly horizonNow = new THREE.Color('#000000');

  constructor(private readonly scene: THREE.Scene) {
    this.domeMat = skyDomeMaterial();   // 67.40: the gradient
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(200, 32, 16), this.domeMat);
    this.dome.renderOrder = -1;          // first among the see-through layers: everything else draws over the sky
    this.dome.frustumCulled = false;
    this.dome.raycast = () => {};
    this.dome.visible = false;
    scene.add(this.dome);
    this.fog = new THREE.Fog(0x9ec4dc, 1e4, 1e5);
    scene.fog = this.fog;
    // the sun: the brightest directional light (renderer.ts createScene)
    let sun: THREE.DirectionalLight | null = null;
    scene.traverse(o => { const l = o as THREE.DirectionalLight; if (l.isDirectionalLight && (!sun || l.intensity > sun.intensity)) sun = l; });
    if (sun) { this.sunLight = sun; this.sunLightDistance = (sun as THREE.DirectionalLight).position.length(); this.sunDir.copy((sun as THREE.DirectionalLight).position).normalize(); this.sunBase = { color: (sun as THREE.DirectionalLight).color.clone(), intensity: (sun as THREE.DirectionalLight).intensity }; }
    scene.traverse(o => {
      const m = (o as THREE.Mesh).material as (FogMaterial & { opacity: number }) | undefined;
      if (o.userData.skyLayer && m) { m.fog = false; this.skyLayers.push({ mat: m, base: m.opacity }); }
    });
  }

  /** Fit the sky to the camera: the planet nearest it, how deep in its air, its colour. */
  update(camera: THREE.Camera, planets: readonly PlanetState[], meshes: Map<number, PlanetMeshData>, weather: SkyWeather | null = null): void {
    const cam = camera.position;
    this.fadeShells(cam, planets, meshes);
    // the planet nearest the camera, and the camera's height above the ground under it (terrain can stand well above the sphere)
    let best: PlanetState | null = null, gap = Infinity;
    for (const p of planets) {
      if (p.type === 'star' || p.type === 'wormhole' || p.alive === false) continue;
      const a = Math.hypot(cam.x - p.position.x, cam.y - p.position.y, cam.z - p.position.z) - p.radius;
      if (a < gap) { gap = a; best = p; }
    }
    let alt = gap;
    if (best && gap < best.radius) {
      const d = { x: cam.x - best.position.x, y: cam.y - best.position.y, z: cam.z - best.position.z };
      alt = Math.max(0, Math.hypot(d.x, d.y, d.z) - getSurfaceRadius(best, d));
    }
    // 74.15: the person's height (the host's, else over hazeGround), eased — for how deep in the air (a crest's lift counted as air
    // on a shallow one) and how far the haze reaches
    const fed = camera.userData.hazeHeight as number | null | undefined;
    camera.userData.hazeHeight = null;   // (taken: a view that stops passing it falls back, never a stale height)
    const overview = Math.min(1, Math.max(0, Number(camera.userData.hazeOverview) || 0));   // (the commander's blend, the host's; taken too)
    camera.userData.hazeOverview = 0;
    const ground = best && typeof fed !== 'number' ? hazeGround(best, FINE_FADE.uFineFocus.value, FINE_FADE.uFineOn.value > 0) : null;
    if (best && typeof fed === 'number' && gap < best.radius) alt = fed;   // (the shoulder camera's)
    else if (best && ground) alt = Math.hypot(cam.x - best.position.x, cam.y - best.position.y, cam.z - best.position.z) - Math.hypot(ground.x - best.position.x, ground.y - best.position.y, ground.z - best.position.z);
    if (best) alt = this.easeHeight(best.id, alt);
    const air: AirProfile | null = best ? airOf(best) : null;
    const inside = best && air ? insideAtmosphere(alt, best.radius, air.rimOverR) : 0;
    const look = best ? skyLook(best) : null;
    if (!best || inside <= 0 || !look) { this.off(meshes); return; }   // 67.40: a tiny world holds no air — bare space
    // daylight: the sun's height over the local horizon — day sky above, night (and the stars) below, dusk between
    const up = new THREE.Vector3(cam.x - best.position.x, cam.y - best.position.y, cam.z - best.position.z).normalize();
    const daySmooth = daylight(up.dot(this.sunDir));
    // the haze takes the horizon's colour (what the far ground fades into), dimmed by night and tinted by the weather
    const shown = look.horizon.clone().multiplyScalar(0.07 + 0.93 * daySmooth);
    const tintAmount = weather && weather.tintAmount > 0 ? weather.tintAmount * inside : 0;
    if (weather && tintAmount > 0) shown.lerp(weather.tint.clone().multiplyScalar(0.1 + 0.9 * daySmooth), tintAmount);   // the weather's tint, inside the air
    const veil = Math.pow(inside, 0.7) * (0.35 + 0.65 * daySmooth);   // at night the air is thinner to the eye: the stars show
    this.dome.visible = true;
    this.dome.position.copy(cam);
    const density = air ? air.density : 0;
    setSkyDome(this.domeMat, look, up, this.sunDir, look.strength * veil, weather?.tint ?? null, tintAmount, density);
    this.tintSun(up.dot(this.sunDir), density * inside);   // 74.10 step 2: a low sun comes through the air red and dim — the ground and the people take the sunset
    for (const l of this.skyLayers) l.mat.opacity = l.base * (1 - veil);
    this.starsShown = 1 - veil; this.localUp = this.upAt.copy(up);
    this.daylightNow = daySmooth; this.airNow = look.strength; this.horizonNow.copy(shown);
    // haze: from half the horizon to four horizons, as deep as the air (the horizon from height h is √(2Rh))
    const horizon = Math.sqrt(2 * best.radius * Math.max(1e-4, alt));
    this.fog.color.copy(shown);
    this.fog.near = 0.5 * horizon / inside;
    this.fog.far = 4 * horizon / Math.max(0.05, inside * look.strength);
    // weather: the fog closes to the sight range (what the sim cannot see, you cannot either), never opening past the clear air's.
    // 74.15: in the person's view only — from a commander's stop or the diorama, hundreds of people up, every ground point lay
    // past the band and the view drew blank; there the clear air's haze holds, the cells drawn as volumes (cellVolumes.ts). The
    // host's commander blend eases one into the other (in log distance, as the weather's own ease)
    let banded = 0;
    if (weather && Number.isFinite(weather.sight) && overview < 1) {
      const band = fogForSight(weather.sight);
      if (band && band.far < this.fog.far) {
        const ease = (clear: number, closed: number) => Math.exp(Math.log(closed) + (Math.log(clear) - Math.log(closed)) * overview);
        this.fog.near = ease(this.fog.near, Math.min(this.fog.near, band.near));
        this.fog.far = ease(this.fog.far, band.far);
        banded = 1 - overview;
      }
    }
    // 74.15: the mountains out of the haze (rendering/hazeLift.ts) — a ground point's fog distance less its own horizon over the
    // eye's ground; off under the sight band (the person sees what the sim does, no further), back with the commander's blend
    HAZE_LIFT.uHazeCentre.value.set(best.position.x, best.position.y, best.position.z);
    HAZE_LIFT.uHazeGround.value = Math.hypot(cam.x - best.position.x, cam.y - best.position.y, cam.z - best.position.z) - alt;
    HAZE_LIFT.uHazeR.value = best.radius;
    HAZE_LIFT.uHazeLiftOn.value = 1 - banded;
    this.hazeOnly(best.id, meshes);
  }

  /** 74.15: the haze's height eased toward `h` (in its log, over HAZE_EASE.seconds), taken at once on a new planet or a jump past HAZE_EASE.snap ×. */
  private easeHeight(planet: number, h: number): number {
    const now = typeof performance !== 'undefined' ? performance.now() / 1000 : 0, v = Math.max(1e-4, h), e = this.eased;
    if (!e || e.planet !== planet || Math.abs(Math.log(v / e.height)) > Math.log(HAZE_EASE.snap)) { this.eased = { planet, height: v, at: now }; return v; }
    const k = 1 - Math.exp(-Math.max(0, now - e.at) / HAZE_EASE.seconds);
    e.height = Math.exp(Math.log(e.height) + (Math.log(v) - Math.log(e.height)) * k); e.at = now;
    return e.height;
  }

  /** Only the planet you are on takes the fog; the others in the sky stay clear (74.10: its clouds with it — a far world's clouds were fog-coloured). */
  private hazeOnly(planetId: number | null, meshes: Map<number, PlanetMeshData>): void {
    if (this.hazed === planetId) return;
    for (const [id, m] of meshes) {
      const mat = m.mesh.material as FogMaterial;
      const want = id === planetId;
      if (mat.fog !== want) { mat.fog = want; mat.needsUpdate = true; }
      // 74.15: the landforms' far strips, the mesh's child, with it (a far world's bands drew fog-coloured)
      const strips = m.mesh.getObjectByName('landformFar') as THREE.Mesh | undefined, sm = strips?.material as FogMaterial | undefined;
      if (sm && sm.fog !== want) { sm.fog = want; sm.needsUpdate = true; }
      if (m.glow) (m.glow.material as FogMaterial).fog = false;
      const cm = m.clouds?.material as FogMaterial | undefined;
      if (cm && cm.fog !== undefined && cm.fog !== want) { cm.fog = want; cm.needsUpdate = true; }
    }
    this.hazed = planetId;
  }

  /** 74.10: the rim and a haze cloud shell fade out where the camera stands at their height (`rimGlowFade`, `hazeBandFade`). */
  private fadeShells(cam: THREE.Vector3, planets: readonly PlanetState[], meshes: Map<number, PlanetMeshData>): void {
    for (const p of planets) {
      if (p.type !== 'planet' || p.alive === false) continue;
      const m = meshes.get(p.id);
      if (!m) continue;
      const d = Math.hypot(cam.x - p.position.x, cam.y - p.position.y, cam.z - p.position.z) / p.radius;
      const rim = m.glow?.userData.rimIntensity as number | undefined;
      const gu = (m.glow?.material as THREE.ShaderMaterial | undefined)?.uniforms;
      if (m.glow && rim !== undefined && gu?.uIntensity) {
        const f = rimGlowFade(d, airOf(p).rimOverR);
        gu.uIntensity.value = rim * f;
        m.glow.visible = f > 0;
      }
      const cu = (m.clouds?.material as THREE.ShaderMaterial | undefined)?.uniforms;
      if (m.clouds && cu?.uFade) cu.uFade.value = hazeBandFade(d, 1 + m.clouds.profile.altitude);
    }
  }

  /** 74.10 step 2: the key light through the air — its colour the scene's × the sun's transmission, its intensity by the transmission's brightness; white and full in space. */
  private tintSun(sinElevation: number, density: number): void {
    this.densityNow = density;
    const t = sunExtinction(sinElevation, density);
    this.sunTint.setRGB(t.r, t.g, t.b);
    if (this.sunLight && this.sunBase) {
      this.sunLight.color.copy(this.sunBase.color).multiply(this.sunTint);
      this.sunLight.intensity = this.sunBase.intensity * tintLuminance(t);
    }
  }

  private off(meshes: Map<number, PlanetMeshData>): void {
    this.starsShown = 1; this.localUp = null; this.eased = null;
    HAZE_LIFT.uHazeLiftOn.value = 0;
    this.daylightNow = 1; this.airNow = 0; this.horizonNow.setRGB(0, 0, 0);
    this.tintSun(1, 0);
    if (!this.dome.visible && this.hazed === null) return;
    this.dome.visible = false;
    for (const l of this.skyLayers) l.mat.opacity = l.base;
    this.fog.near = 1e4; this.fog.far = 1e5;
    this.hazeOnly(null, meshes);
  }

  dispose(): void {
    this.scene.remove(this.dome);
    this.dome.geometry.dispose(); this.domeMat.dispose();
    if (this.scene.fog === this.fog) this.scene.fog = null;
    for (const l of this.skyLayers) l.mat.opacity = l.base;
  }
}
